import { useEffect, useState } from "react";
import {
    Alert, Box, Button, Chip, Divider, Grid, IconButton,
    InputAdornment, Paper, TextField, Typography
} from "@mui/material";
import SimpleBackdrop from "../../components/Loading/SimpleBackdrop";
import { AlertInfo, AlertConfirm } from "../../components/Alerts/Alert";
import { api } from "../../services/api";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import KeyIcon from '@mui/icons-material/Key';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

export default function TabAsaas() {
    const [openLoad, setOpenLoad] = useState(false);
    const [api_key_asaas, setApiKeyAsaas] = useState('');
    const [showKey, setShowKey] = useState(false);
    const [testStatus, setTestStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [testMsg, setTestMsg] = useState('');
    const [nomeContaAsaas, setNomeContaAsaas] = useState('');

    async function loadData() {
        setOpenLoad(true);
        try {
            const response = await api.get('/escritorio');
            const data = response.data;
            if (data) {
                const info = Array.isArray(data) ? data[0] : data;
                setApiKeyAsaas(info?.api_key_asaas || '');
                if (info?.api_key_asaas) setTestStatus('idle');
            }
        } catch (error) {
            AlertInfo({ icon: 'error', title: 'Falha ao carregar dados' });
        }
        setOpenLoad(false);
    }

    async function handleTestar() {
        if (!api_key_asaas.trim()) return AlertInfo({ icon: 'info', title: 'Informe a chave API antes de testar' });
        setOpenLoad(true);
        setTestStatus('idle');
        setTestMsg('');
        try {
            const response = await api.post('/testarasaas', { api_key_asaas });
            if (response.data.success) {
                setTestStatus('success');
                setNomeContaAsaas(response.data.nomeEscritorio || '');
                setTestMsg(`Conexão realizada com sucesso! Conta: ${response.data.nomeEscritorio || 'Verificada'}`);
            } else {
                setTestStatus('error');
                setTestMsg(response.data.msg || 'Chave inválida ou sem acesso ao Asaas');
            }
        } catch (error) {
            setTestStatus('error');
            setTestMsg('Falha ao conectar com o servidor');
        }
        setOpenLoad(false);
    }

    async function handleSave() {
        if (!api_key_asaas.trim()) return AlertInfo({ icon: 'info', title: 'Informe a chave API antes de salvar' });
        setOpenLoad(true);
        try {
            const response = await api.post('/salvarconfiguracaoasaas', { api_key_asaas });
            if (response.data.success) {
                const webhookMsg = response.data.webhookRegistrado
                    ? '\nWebhook registrado automaticamente no Asaas ✓'
                    : '\nAviso: ' + response.data.webhookMsg;
                AlertInfo({ icon: 'success', title: 'Chave API salva com sucesso!' + webhookMsg });
                setTestStatus('success');
                setTestMsg('Chave válida e salva. ' + (response.data.webhookRegistrado ? 'Webhook configurado automaticamente.' : response.data.webhookMsg));
            } else {
                AlertInfo({ icon: 'error', title: response.data.msg || 'Falha ao salvar' });
            }
        } catch (error) {
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setOpenLoad(false);
    }

    async function handleRemover() {
        setOpenLoad(true);
        try {
            await api.post('/updateescritorio', { api_key_asaas: null });
            setApiKeyAsaas('');
            setTestStatus('idle');
            AlertInfo({ icon: 'success', title: 'Chave removida com sucesso' });
        } catch (error) {
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setOpenLoad(false);
    }

    async function handleImportar() {
        if (!api_key_asaas.trim()) return AlertInfo({ icon: 'info', title: 'Configure a chave API antes de importar' });
        const confirm = await AlertConfirm({
            title: 'Importar boletos do Asaas?',
            text: 'Serão importados todos os boletos com status pendente ou em atraso da sua conta Asaas. Clientes não cadastrados serão criados automaticamente.',
        });
        if (!confirm.isConfirmed) return;
        setOpenLoad(true);
        try {
            const response = await api.post('/importarboletosasaas');
            if (response.data.success) {
                const { importados, clientesCriados, jaExistentes, ignorados = 0, outrasFormas = 0 } = response.data;
                let texto = `${importados} cobrança${importados !== 1 ? 's' : ''} importada${importados !== 1 ? 's' : ''}.`;
                if (clientesCriados > 0) texto += `\n${clientesCriados} cliente${clientesCriados !== 1 ? 's' : ''} cadastrado${clientesCriados !== 1 ? 's' : ''}.`;
                if (jaExistentes > 0) texto += `\n${jaExistentes} já existente${jaExistentes !== 1 ? 's' : ''} no sistema (ignorado${jaExistentes !== 1 ? 's' : ''}).`;
                if (outrasFormas > 0) texto += `\n${outrasFormas} cobrança${outrasFormas !== 1 ? 's' : ''} de PIX/cartão não ${outrasFormas !== 1 ? 'foram importadas' : 'foi importada'} (somente boletos).`;
                if (ignorados > 0) texto += `\n${ignorados} não ${ignorados !== 1 ? 'puderam' : 'pôde'} ser importada${ignorados !== 1 ? 's' : ''} agora. Tente importar novamente.`;
                AlertInfo({ icon: importados > 0 ? 'success' : 'info', title: importados > 0 ? 'Importação concluída!' : 'Nenhuma nova cobrança', text: texto });
            } else {
                AlertInfo({ icon: 'error', title: response.data.msg || 'Falha ao importar' });
            }
        } catch {
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setOpenLoad(false);
    }

    useEffect(() => {
        loadData();
    }, []);

    return (
        <>
            <SimpleBackdrop open={openLoad} />
            <Grid container spacing={2} justifyContent="center">

                {/* Card principal de configuração */}
                <Grid item xs={12} sm={9} md={7}>
                    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                        <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                            <KeyIcon color="primary" sx={{ fontSize: 30 }} />
                            <Box>
                                <Typography variant="h6" fontWeight="bold" lineHeight={1.2}>
                                    Integração Asaas
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Configuração da chave API para emissão de boletos e carnês
                                </Typography>
                            </Box>
                            {api_key_asaas ? (
                                <Chip label="CONFIGURADO" color="success" size="small" sx={{ ml: 'auto' }} />
                            ) : (
                                <Chip label="NÃO CONFIGURADO" color="warning" size="small" sx={{ ml: 'auto' }} />
                            )}
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        <Alert severity="info" sx={{ mb: 3 }}>
                            Para emitir <strong>boletos</strong> e <strong>carnês</strong> para seus clientes pelo módulo
                            Contas a Receber, você precisa de uma conta Asaas e configurar sua chave API aqui.
                        </Alert>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Chave API do Asaas
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    placeholder="$aact_YTU5YmY4ZGI2YWRhMjY4..."
                                    value={api_key_asaas}
                                    onChange={e => {
                                        setApiKeyAsaas(e.target.value);
                                        setTestStatus('idle');
                                    }}
                                    type={showKey ? 'text' : 'password'}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={() => setShowKey(!showKey)} edge="end" size="small">
                                                    {showKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        sx: { fontFamily: 'monospace', fontSize: 13 }
                                    }}
                                />
                            </Grid>

                            {/* Resultado do teste */}
                            {testStatus !== 'idle' && (
                                <Grid item xs={12}>
                                    <Alert
                                        severity={testStatus === 'success' ? 'success' : 'error'}
                                        icon={testStatus === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
                                    >
                                        {testMsg}
                                    </Alert>
                                </Grid>
                            )}

                            {/* Botões de ação */}
                            <Grid item xs={12} sm={4}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    color="info"
                                    onClick={handleTestar}
                                    disabled={!api_key_asaas.trim()}
                                >
                                    TESTAR CONEXÃO
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    color="primary"
                                    onClick={handleSave}
                                    disabled={!api_key_asaas.trim()}
                                >
                                    SALVAR CHAVE
                                </Button>
                            </Grid>
                            {api_key_asaas && (
                                <Grid item xs={12} sm={4}>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        color="error"
                                        onClick={handleRemover}
                                    >
                                        REMOVER CHAVE
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                </Grid>

                {/* Card de importação */}
                {api_key_asaas && (
                    <Grid item xs={12} sm={9} md={7}>
                        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                            <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                                <CloudDownloadIcon color="primary" sx={{ fontSize: 28 }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
                                        Importar Boletos
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Importe boletos pendentes e em atraso da sua conta Asaas para o sistema
                                    </Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    startIcon={<CloudDownloadIcon />}
                                    onClick={handleImportar}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Importar Boletos
                                </Button>
                            </Box>
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                Boletos já existentes no sistema serão ignorados. Clientes não cadastrados serão criados automaticamente com os dados do Asaas.
                            </Alert>
                        </Paper>
                    </Grid>
                )}

                {/* Card de instruções */}
                <Grid item xs={12} sm={9} md={7}>
                    <Paper elevation={1} sx={{ p: 3, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Como obter sua chave API Asaas
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={1}>
                            {[
                                { step: '1', text: 'Acesse www.asaas.com e faça login em sua conta', bold: 'www.asaas.com' },
                                { step: '2', text: 'No menu lateral, clique em Integrações', bold: 'Integrações' },
                                { step: '3', text: 'Clique em API Key', bold: 'API Key' },
                                { step: '4', text: 'Copie a Chave de API (começa com $aact_)', bold: '$aact_' },
                                { step: '5', text: 'Cole a chave acima e clique em Testar Conexão', bold: 'Testar Conexão' },
                                { step: '6', text: 'Se a conexão for OK, clique em Salvar Chave', bold: 'Salvar Chave' },
                            ].map(item => (
                                <Grid item xs={12} key={item.step}>
                                    <Box display="flex" alignItems="flex-start" gap={1.5}>
                                        <Chip label={item.step} size="small" color="primary" sx={{ minWidth: 28, height: 24 }} />
                                        <Typography variant="body2">{item.text}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                        <Box mt={2}>
                            <Button
                                variant="text"
                                size="small"
                                startIcon={<OpenInNewIcon />}
                                onClick={() => window.open('https://www.asaas.com/login', '_blank')}
                            >
                                Abrir Asaas
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

            </Grid>
        </>
    );
}
